import { useState, useMemo } from 'react';
import { Segmented, Drawer, Modal, Skeleton } from 'antd';
import { LoginOutlined, LogoutOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DataGrid, type DataGridColumn } from '../../../components/gorkem-components';
import '../../../components/gorkem-components/styles/index.css';
import type { FaaliyetRow, FaaliyetOzetRow, BelgeGrupRow, FaaliyetFilterType } from '../utils/calculations';
import type { TieredQueryResult } from '../hooks/useTieredQueries';

// Faaliyet özet tablosu kolonları
const faaliyetOzetColumns: DataGridColumn<FaaliyetOzetRow & Record<string, unknown>>[] = [
  { id: 'ISLEMIZAHAT', header: 'İşlem İzahat', accessorKey: 'ISLEMIZAHAT', width: 200, enableGrouping: true },
  { id: 'TARIH', header: 'Tarih', accessorKey: 'TARIH', cellRenderer: 'date', width: 100 },
  { id: 'TLGIREN', header: 'Giren', accessorKey: 'TLGIREN', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 120 },
  { id: 'TLCIKAN', header: 'Çıkan', accessorKey: 'TLCIKAN', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 120 },
  { id: 'TLTOPLAM', header: 'Toplam', accessorKey: 'TLTOPLAM', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 120 },
];

// Belge grup kolonları
const belgeGrupColumns: DataGridColumn<BelgeGrupRow & Record<string, unknown>>[] = [
  {
    id: 'FIRMAADI_BELGENO', header: 'Firma / Belge No', accessorKey: 'FIRMAADI', width: 280,
    cell: ({ row }) => {
      const data = row.original as BelgeGrupRow;
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{data.FIRMAADI}</span>
          <span style={{ background: '#eff6ff', color: '#1e40af', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
            {data.BELGENO}
          </span>
        </span>
      );
    },
  },
  { id: 'TARIH', header: 'Tarih', accessorKey: 'TARIH', cellRenderer: 'date', width: 100 },
  { id: 'TLGIREN', header: 'Giren', accessorKey: 'TLGIREN', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 130 },
  { id: 'TLCIKAN', header: 'Çıkan', accessorKey: 'TLCIKAN', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 130 },
  { id: 'TLTOPLAM', header: 'Toplam', accessorKey: 'TLTOPLAM', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 130 },
  { id: 'KAYITSAYISI', header: 'Kayıt', accessorKey: 'KAYITSAYISI', width: 70 },
];

// Belge detay kolonları
const belgeDetayColumns: DataGridColumn<FaaliyetRow & Record<string, unknown>>[] = [
  { id: 'OZELKOD1', header: 'Şube', accessorKey: 'OZELKOD1', width: 120 },
  { id: 'ACIKLAMA', header: 'Açıklama', accessorKey: 'ACIKLAMA', width: 200 },
  { id: 'IZAHAT', header: 'İzahat', accessorKey: 'IZAHAT', width: 150 },
  { id: 'TLGIREN', header: 'Giren', accessorKey: 'TLGIREN', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 120 },
  { id: 'TLCIKAN', header: 'Çıkan', accessorKey: 'TLCIKAN', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 120 },
  { id: 'TLTOPLAM', header: 'Toplam', accessorKey: 'TLTOPLAM', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2 }, aggregationType: 'sum', width: 120 },
  { id: 'BAKIYE', header: 'Bakiye', accessorKey: 'BAKIYE', cellRenderer: 'currency', cellParams: { currency: 'TRY', decimals: 2, colorCoded: true }, width: 130 },
];

const formatTL = (value: number) =>
  (value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function SummaryCards({ giren, cikan, toplam }: { giren: number; cikan: number; toplam: number }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
      <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 20px', textAlign: 'center', flex: '1 1 auto', minWidth: 140 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#166534', whiteSpace: 'nowrap' }}>{formatTL(giren)} ₺</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Toplam Giren</div>
      </div>
      <div style={{ background: '#fef2f2', borderRadius: 10, padding: '12px 20px', textAlign: 'center', flex: '1 1 auto', minWidth: 140 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#b91c1c', whiteSpace: 'nowrap' }}>{formatTL(cikan)} ₺</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Toplam Çıkan</div>
      </div>
      <div style={{ background: '#faf6f2', borderRadius: 10, padding: '12px 20px', textAlign: 'center', flex: '1 1 auto', minWidth: 140 }}>
        <div style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>{formatTL(toplam)} ₺</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Net Toplam</div>
      </div>
    </div>
  );
}

interface FaaliyetSectionProps {
  queries: TieredQueryResult;
  faaliyetFilter: FaaliyetFilterType;
  onFaaliyetFilterChange: (filter: FaaliyetFilterType) => void;
}

export function FaaliyetSection({ queries, faaliyetFilter, onFaaliyetFilterChange }: FaaliyetSectionProps) {
  const { tier2Batch, getResult } = queries;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedGrup, setSelectedGrup] = useState<FaaliyetOzetRow | null>(null);
  const [selectedBelge, setSelectedBelge] = useState<BelgeGrupRow | null>(null);
  const [belgeModalOpen, setBelgeModalOpen] = useState(false);

  const faaliyetLoading = tier2Batch.isLoading;
  const faaliyetRows = (getResult<FaaliyetRow>('tier2', 'faaliyet')?.data || []) as FaaliyetRow[];

  // Giriş gruplanmış
  const girisGruplu = useMemo((): FaaliyetOzetRow[] => {
    const rows = faaliyetRows.filter(row => row.ISLEM === 'Giriş');
    const grouped = rows.reduce((acc, row) => {
      const key = `${row.TARIH}_${row.ISLEMIZAHAT}`;
      if (!acc[key]) {
        acc[key] = { TARIH: row.TARIH, ISLEMIZAHAT: row.ISLEMIZAHAT, ISLEM: row.ISLEM, TLGIREN: 0, TLCIKAN: 0, TLTOPLAM: 0, KAYITSAYISI: 0 };
      }
      acc[key].TLGIREN += row.TLGIREN || 0;
      acc[key].TLCIKAN += row.TLCIKAN || 0;
      acc[key].TLTOPLAM += row.TLTOPLAM || 0;
      acc[key].KAYITSAYISI += 1;
      return acc;
    }, {} as Record<string, FaaliyetOzetRow>);
    return Object.values(grouped).sort((a, b) => dayjs(b.TARIH).valueOf() - dayjs(a.TARIH).valueOf());
  }, [faaliyetRows]);

  // Çıkış gruplanmış
  const cikisGruplu = useMemo((): FaaliyetOzetRow[] => {
    const rows = faaliyetRows.filter(row => row.ISLEM === 'Çıkış');
    const grouped = rows.reduce((acc, row) => {
      const key = `${row.TARIH}_${row.ISLEMIZAHAT}`;
      if (!acc[key]) {
        acc[key] = { TARIH: row.TARIH, ISLEMIZAHAT: row.ISLEMIZAHAT, ISLEM: row.ISLEM, TLGIREN: 0, TLCIKAN: 0, TLTOPLAM: 0, KAYITSAYISI: 0 };
      }
      acc[key].TLGIREN += row.TLGIREN || 0;
      acc[key].TLCIKAN += row.TLCIKAN || 0;
      acc[key].TLTOPLAM += row.TLTOPLAM || 0;
      acc[key].KAYITSAYISI += 1;
      return acc;
    }, {} as Record<string, FaaliyetOzetRow>);
    return Object.values(grouped).sort((a, b) => dayjs(b.TARIH).valueOf() - dayjs(a.TARIH).valueOf());
  }, [faaliyetRows]);

  // Belge gruplanmış (drawer)
  const belgeGruplu = useMemo((): BelgeGrupRow[] => {
    if (!selectedGrup) return [];
    const filtered = faaliyetRows.filter(row =>
      row.TARIH === selectedGrup.TARIH &&
      row.ISLEMIZAHAT === selectedGrup.ISLEMIZAHAT &&
      row.ISLEM === selectedGrup.ISLEM
    );
    const grouped = filtered.reduce((acc, row) => {
      const key = `${row.BELGENO}_${row.FIRMAADI}`;
      if (!acc[key]) {
        acc[key] = { BELGENO: row.BELGENO || '', FIRMAADI: row.FIRMAADI || '', TARIH: row.TARIH, ISLEM: row.ISLEM, TLGIREN: 0, TLCIKAN: 0, TLTOPLAM: 0, KAYITSAYISI: 0, detaylar: [] };
      }
      acc[key].TLGIREN += row.TLGIREN || 0;
      acc[key].TLCIKAN += row.TLCIKAN || 0;
      acc[key].TLTOPLAM += row.TLTOPLAM || 0;
      acc[key].KAYITSAYISI += 1;
      acc[key].detaylar.push(row);
      return acc;
    }, {} as Record<string, BelgeGrupRow>);
    return Object.values(grouped).sort((a, b) => (a.FIRMAADI || '').localeCompare(b.FIRMAADI || ''));
  }, [faaliyetRows, selectedGrup]);

  const handleRowClick = (row: FaaliyetOzetRow) => {
    setSelectedGrup(row);
    setDrawerOpen(true);
  };

  const aggregations = { TLGIREN: 'sum' as const, TLCIKAN: 'sum' as const, TLTOPLAM: 'sum' as const };

  return (
    <div style={styles.chartsSection}>
      <div style={styles.chartsSectionHeader}>
        <h2 style={styles.chartsSectionTitle}>Son Faaliyetler</h2>
        <Segmented
          options={[
            { label: 'Bugün', value: 'today' },
            { label: 'Bu Hafta', value: 'thisWeek' },
            { label: 'Bu Ay', value: 'thisMonth' },
          ]}
          value={faaliyetFilter}
          onChange={(value) => onFaaliyetFilterChange(value as FaaliyetFilterType)}
          style={{ background: 'var(--color-header-bg)' }}
        />
      </div>

      {!queries.tier1Ready || faaliyetLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {[0, 1].map(i => (
            <div key={i} style={styles.tableContainer}>
              <div style={{ ...styles.tableHeader, background: i === 0 ? '#166534' : '#b91c1c' }}>
                <div style={styles.tableHeaderTitle}>{i === 0 ? 'GİRİŞ' : 'ÇIKIŞ'}</div>
              </div>
              <div style={{ padding: 20 }}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="faaliyet-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Giriş */}
          <div style={styles.tableContainer}>
            <div style={{ ...styles.tableHeader, background: '#166534' }}>
              <div style={styles.tableHeaderTitle}><LoginOutlined style={{ marginRight: 8 }} />GİRİŞ</div>
            </div>
            <DataGrid<FaaliyetOzetRow & Record<string, unknown>>
              data={girisGruplu as (FaaliyetOzetRow & Record<string, unknown>)[]}
              columns={faaliyetOzetColumns}
              loading={faaliyetLoading}
              groupBy={['ISLEMIZAHAT']}
              enableSorting enableSearch enablePagination={false}
              theme="finance-light" density="compact" storageKey="faaliyet-giris-ozet"
              onRowClick={handleRowClick}
              aggregations={aggregations}
              emptyMessage="Giriş kaydı bulunamadı"
            />
          </div>

          {/* Çıkış */}
          <div style={styles.tableContainer}>
            <div style={{ ...styles.tableHeader, background: '#b91c1c' }}>
              <div style={styles.tableHeaderTitle}><LogoutOutlined style={{ marginRight: 8 }} />ÇIKIŞ</div>
            </div>
            <DataGrid<FaaliyetOzetRow & Record<string, unknown>>
              data={cikisGruplu as (FaaliyetOzetRow & Record<string, unknown>)[]}
              columns={faaliyetOzetColumns}
              loading={faaliyetLoading}
              groupBy={['ISLEMIZAHAT']}
              enableSorting enableSearch enablePagination={false}
              theme="finance-light" density="compact" storageKey="faaliyet-cikis-ozet"
              onRowClick={handleRowClick}
              aggregations={aggregations}
              emptyMessage="Çıkış kaydı bulunamadı"
            />
          </div>
        </div>
      )}

      {/* Faaliyet Detay Drawer */}
      <Drawer
        title={selectedGrup && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40,
              background: selectedGrup.ISLEM === 'Giriş' ? '#dcfce7' : '#fee2e2',
              borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: selectedGrup.ISLEM === 'Giriş' ? '#166534' : '#b91c1c', fontSize: 18,
            }}>
              {selectedGrup.ISLEM === 'Giriş' ? <LoginOutlined /> : <LogoutOutlined />}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedGrup.ISLEMIZAHAT}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {dayjs(selectedGrup.TARIH).format('DD.MM.YYYY')} • {selectedGrup.KAYITSAYISI} kayıt
              </div>
            </div>
          </div>
        )}
        placement="right" width={950}
        onClose={() => setDrawerOpen(false)} open={drawerOpen}
        styles={{ body: { padding: '20px 24px', display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        {selectedGrup && (
          <>
            <SummaryCards giren={selectedGrup.TLGIREN} cikan={selectedGrup.TLCIKAN} toplam={selectedGrup.TLTOPLAM} />
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
              Belgeler ({belgeGruplu.length} adet) - Detay için satıra tıklayın
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <DataGrid<BelgeGrupRow & Record<string, unknown>>
                data={belgeGruplu as (BelgeGrupRow & Record<string, unknown>)[]}
                columns={belgeGrupColumns}
                loading={false}
                enableExcelExport excelFilename={`faaliyet-belgeler-${selectedGrup.ISLEMIZAHAT}`}
                enableSorting enableSearch enablePagination pageSize={50}
                theme="finance-light" density="compact" storageKey="faaliyet-belgeler"
                onRowClick={(row) => { setSelectedBelge(row as BelgeGrupRow); setBelgeModalOpen(true); }}
                aggregations={aggregations}
                emptyMessage="Belge kaydı bulunamadı"
              />
            </div>
          </>
        )}
      </Drawer>

      {/* Belge Detay Modal */}
      <Modal
        title={selectedBelge && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileTextOutlined style={{ fontSize: 20, color: '#1e40af' }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedBelge.FIRMAADI}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Belge No: {selectedBelge.BELGENO} • {dayjs(selectedBelge.TARIH).format('DD.MM.YYYY')}
              </div>
            </div>
          </div>
        )}
        open={belgeModalOpen} onCancel={() => setBelgeModalOpen(false)}
        footer={null} width={900}
        styles={{ body: { padding: '20px 24px' } }}
      >
        {selectedBelge && (
          <>
            <SummaryCards giren={selectedBelge.TLGIREN} cikan={selectedBelge.TLCIKAN} toplam={selectedBelge.TLTOPLAM} />
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
              Belge İçeriği ({selectedBelge.detaylar.length} kalem)
            </div>
            <DataGrid<FaaliyetRow & Record<string, unknown>>
              data={selectedBelge.detaylar as (FaaliyetRow & Record<string, unknown>)[]}
              columns={belgeDetayColumns}
              loading={false}
              enableExcelExport excelFilename={`belge-detay-${selectedBelge.BELGENO}`}
              enableSorting enableSearch enablePagination={false}
              theme="finance-light" density="compact" storageKey="belge-detay"
              aggregations={aggregations}
              emptyMessage="Belge içeriği bulunamadı"
            />
          </>
        )}
      </Modal>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  chartsSection: { marginTop: 8 },
  chartsSectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16, flexWrap: 'wrap' as const, gap: 12,
  },
  chartsSectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 },
  tableContainer: {
    background: 'var(--color-card-bg)', borderRadius: 12, overflow: 'hidden',
    boxShadow: '0 1px 3px var(--shadow-color)',
  },
  tableHeader: { padding: '12px 16px', color: '#ffffff', textAlign: 'center' as const },
  tableHeaderTitle: { fontSize: 16, fontWeight: 700, letterSpacing: '0.1em' },
};
